// @flow
import * as React from 'react';
import ReactDOM from 'react-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Box } from 'grommet';
import styled from 'styled-components';
import { getSidePanel } from 'esm-web-pro/redux/side-panel';
import * as sidePanelActionCreators from 'esm-web-pro/redux/side-panel/actions';
import { type SidePanel } from 'esm-web-pro/redux/side-panel/reducer';
import UpperContainer from 'esm-web-pro/components/layout/sidePanel/UpperContainer';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import Title from 'esm-web-pro/components/layout/sidePanel/Title';
import { shallowEqual } from 'speed-js-core';
import SidePaneContext from './context/SidePaneContext';


type Props = {
    actions?: React.Node,
    title: ?(Object | string),
    titleLabel?: string | React.Node,

    titleIcon?: React.Node,
    titleOnView?: Function,

    renderTitle: ?() => React.Node,

    children: Object,
    width?: "xxsmall" | "xsmall" | "small" | "medium" | "large" | "xlarge" | "xxlarge",
    zIndex: number | string,

    id: string,
    url: ?string,
    openInitially?: boolean,
    closeAllOpened: boolean,
    sensitive?: boolean | {
        message: string
    },
    continuity?: boolean,
    closeIfNotTopmost?: boolean,

    // SidePanel redux props
    sidePanel: ?SidePanel,
    openPanel?: typeof sidePanelActionCreators.openPanel,
    closePanel: ?(key: string, skipSensitivity: boolean) => { key: string },
    closeTopmost: ?(skipSensitivity: boolean) => {},
    closeAllPanels: ?(skipSensitivity: boolean) => {},
    setTitle: ?(id: string, title: string) => {},
};

type State = {
    shadowTop: boolean,
    shadowBottom: boolean,
};

const StyledBox = styled(Box)`
    position: absolute;
    height: 100%;
    width: 100%;
    min-height: 100%;
    display: block;
    z-index: ${(props) => props.zIndex};
`;

const StyledContentDiv = styled.div`
  height: 100%;
  margin-top: 56px;
  margin-bottom: ${(props) => (props.marginBottom ? '56px' : 'none')};
  overflow: auto;
  position: relative;
  &::before {
    content: "";
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    // box-shadow: ${(props) => (props.shadow ? 'inset 0 -8px 10px #c0bfbf' : 'none')};
    // box-shadow: ${(props) => (props.shadow ? 'inset 0 -9px 0px #c0bfbf' : 'none')};
    pointer-events: none;
  }
`;

export const StyledShadow = styled.div`
  position: absolute;
  bottom: 0;
  // TODO: See why z-index 1000 is needed
  //z-index: 1000;
  left: 0;
  right: 0;
  background-color: #fff;
  padding-bottom: ${(props) => ((props.shadow && props.actions) ? '52px' : 'none')};
  box-shadow: ${(props) => (props.shadow ? '0px 0px 10px #c0bfbf' : 'none')};
}
`;

const StyledTitleBox = styled(Box)`
  position: absolute;
  top: 0;
  //z-index: 1;
  left: 0;
  right: 0;
  background-color: #fff;
  box-shadow: ${(props) => (props.shadow ? '0 10px 10px -10px #c0bfbf' : 'none')} ;
`;

const mapStateToProps = (state, ownProps) => ({
    sidePanel: (() => getSidePanel(state, ownProps.id))(),
});

const mapDispatchToProps = (dispatch) => (bindActionCreators(sidePanelActionCreators, dispatch));

@connect(mapStateToProps, mapDispatchToProps)
class SidePane extends React.Component<Props, State> {
    static contextType = SidePaneContext;

    static defaultProps = {
        url: null,
        openInitially: false,
        closeAllOpened: false,
        sensitive: false,
        continuity: false,
        closeIfNotTopmost: false,
        title: null,
        titleLabel: null,
        setTitle: undefined,
        renderTitle: undefined,
        zIndex: 'auto',

        width: undefined,

        actions: null,
        titleIcon: null,
        titleOnView: null,

        sidePanel: undefined,
        openPanel: undefined,
        closePanel: undefined,
        closeTopmost: undefined,
        closeAllPanels: undefined,
    };

    unmounting: boolean = false;

    constructor(props: Props) {
        super(props);
        this.state = {
            shadowTop: false,
            shadowBottom: false,
        };
    }

    componentDidMount() {
        const {
            openInitially, closeAllOpened, closeAllPanels, openPanel, id, url, sensitive, continuity,
            closeIfNotTopmost, title,
        } = this.props;

        if (openInitially && openPanel) {
            if (closeAllOpened && closeAllPanels) {
                closeAllPanels(true);
            }
            openPanel(id, title, url, sensitive, continuity, closeIfNotTopmost);
        }
        this.getShadows();
        window.addEventListener('resize', this.getShadows);
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (!shallowEqual(prevProps, this.props)) {
            this.getShadows();
        }
        if (this.props.sidePanel && this.props.title !== prevProps.title && typeof this.props.title === 'string') {
            const { id, setTitle, title } = this.props;
            setTitle(id, title);
        }
    }

    componentWillUnmount() {
        this.unmounting = true;
        window.removeEventListener('resize', this.getShadows);
        const { sidePanel, closePanel, id } = this.props;
        if (sidePanel && closePanel) {
            closePanel(id, true);
        }
    }

    // Try to make it work without setTimeout()
    getShadows = () => {
        const elements = document.querySelectorAll('.content-div');
        let element = null;

        if (elements) {
            element = elements[elements.length - 1];
        }
        if (element) {
            setTimeout(() => {
                if (this.unmounting) return;
                // $FlowFixMe
                const { scrollHeight, clientHeight, scrollTop } = element;
                // eslint-disable-next-line max-len
                this.setState({ shadowBottom: scrollHeight > clientHeight || scrollTop === 0, shadowTop: scrollTop !== 0 });
            }, 5);
        }
    }


    handleScroll = (e: SyntheticEvent<HTMLDivElement>) => {
        if (e.target.className && e.target.className.includes('select__menu-list')) {
            return;
        }
        e.stopPropagation();
        // $FlowFixMe
        const { scrollTop, scrollHeight, offsetHeight } = e.target;
        const shadowTop = scrollTop !== 0;
        const differenceScroll = Math.floor(scrollHeight - offsetHeight);
        let shadowBottom = false;
        if (Math.floor(scrollTop) !== differenceScroll
            && Math.ceil(scrollTop) !== differenceScroll) {
            shadowBottom = true;
        }
        this.setState({
            shadowTop,
            shadowBottom,
        });
    }

    render() {
        const {
            children, title, width, sidePanel, titleLabel, zIndex, actions, titleIcon, titleOnView, id, renderTitle,
        } = this.props;
        const { shadowTop, shadowBottom } = this.state;
        if (!sidePanel) return null;

        const innerContent = (
            <SidePaneContext.Consumer>
                {(context) => {
                    const containerProps = {};

                    if (context?.closed) {
                        containerProps.style = { display: 'none' };
                    }

                    return (
                        <div {...containerProps}>
                            <Box width={width} />
                            <StyledBox
                                height="100%"
                                background={{ color: 'white' }}
                                style={{ right: '0px' }}
                                width={width}
                                zIndex={zIndex}
                                opened={context?.closed}
                            >
                                <Box
                                    position="relative"
                                    height="100%"
                                >
                                    {title && !renderTitle && (
                                        <>
                                            <StyledTitleBox
                                            // If the box height is smaller than the bar on the left change it to 66px
                                                height="56px"
                                                shadow={shadowTop}
                                                pad={{ horizontal: 'small' }}
                                            >
                                                <Title
                                                    label={titleLabel}
                                                    text={title}
                                                    icon={titleIcon}
                                                    onView={titleOnView}
                                                />
                                            </StyledTitleBox>
                                            <UpperContainer>
                                                <HorizontalLine color="grey" />
                                            </UpperContainer>
                                        </>
                                    )}
                                    {renderTitle ? renderTitle() : null}
                                    <StyledContentDiv
                                        className="content-div"
                                        marginBottom={actions}
                                        shadow={!actions && shadowBottom}
                                        onScroll={this.handleScroll}
                                        key={id}
                                    >
                                        {children}
                                    </StyledContentDiv>
                                    <StyledShadow shadow={shadowBottom} actions={actions}>
                                        {actions}
                                    </StyledShadow>
                                </Box>
                            </StyledBox>
                        </div>
                    );
                }}
            </SidePaneContext.Consumer>
        );

        const portalRoot = document.getElementById('side-pane');
        if (portalRoot) {
            return ReactDOM.createPortal(innerContent, portalRoot);
        }
        return null;
    }
}

export default SidePane;
